import axios from 'axios'
import Cookies from 'js-cookie'
import { AuthResponse, RegisterData, User } from '../types/auth'
import { Recommendation, Subscription, SubscriptionPlan, SubscriptionSummary, SubscriptionCreate, Notification, KYCData, Blog, BlogListResponse, HeroSettings, HeroSettingsResponse } from '../types'
import { normalizeKYCDate } from '../utils/kycFlow'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('token')
    console.log('[API] Request', config.method?.toUpperCase(), config.url, { hasToken: Boolean(token) })
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log('[API] Response', response.config.method?.toUpperCase(), response.config.url, response.status)
    return response
  },
  (error) => {
    console.error('[API] Response error', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status, error.message)
    if (error.response?.status === 401) {
      Cookies.remove('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Helper to cache API responses for a short duration to prevent duplicate simultaneous calls
export const memoizePromise = <T>(fn: () => Promise<T>, ttlMs = 3000): { (): Promise<T>; clear: () => void } => {
  let cache: { promise: Promise<T>; timestamp: number } | null = null
  const memoized = () => {
    const now = Date.now()
    if (cache && now - cache.timestamp < ttlMs) {
      return cache.promise
    }
    const promise = fn()
    cache = { promise, timestamp: now }
    promise.catch(() => {
      cache = null
    })
    return promise
  }
  memoized.clear = () => {
    cache = null
  }
  return memoized
}

// Auth API
export const authApi = {
  /** OTP login: step 1 — send OTP to email */
  sendOtp: async (email: string): Promise<{ status: string; email: string }> => {
    const response = await api.post('/auth/send-otp', { email })
    return response.data
  },

  /** OTP login: step 2 — verify OTP and receive token */
  verifyOtp: async (email: string, otp_code: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/verify-otp', { email, otp_code })
    return response.data
  },

  /** Google OAuth login — send auth code (preferred) or id_token, receive JWT */
  googleLogin: async (payload: { code?: string; id_token?: string; redirect_uri?: string }): Promise<AuthResponse> => {
    const response = await api.post('/auth/google-login', payload)
    return response.data
  },

  /** Registration: create user + send OTP (no password) */
  register: async (data: RegisterData): Promise<{ status: string; email: string }> => {
    const response = await api.post('/auth/register', data)
    return response.data
  },

  /** Check if email exists. If yes, OTP is sent automatically. */
  checkEmail: async (email: string): Promise<{
    exists: boolean
    has_active_plan?: boolean
    needs_profile?: boolean
    full_name?: string
  }> => {
    const response = await api.post('/auth/check-email', { email })
    return response.data
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me')
    return response.data
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout')
  },
}

// User API
export const userApi = {
  getProfile: async (): Promise<User> => {
    const response = await api.get('/users/profile')
    return response.data
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.put('/users/profile', data)
    return response.data
  },

  getPublicConfig: async (): Promise<{ hero_image_url: string | null; updated_at: string | null }> => {
    const response = await api.get('/users/public-config')
    return response.data
  },

  getHeroSettings: async (): Promise<HeroSettingsResponse> => {
    const response = await api.get('/users/hero-settings')
    return response.data
  },
}

export const adminSettingsApi = {
  getHeroSettings: async (): Promise<HeroSettingsResponse> => {
    const response = await api.get('/admin/hero-settings')
    return response.data
  },

  updateHeroSettings: async (
    heroSettings: HeroSettings,
    files: {
      profileImageFile?: File | null
    },
    onUploadProgress?: (progressPercent: number) => void,
  ): Promise<HeroSettingsResponse> => {
    const formData = new FormData()
    formData.append('payload', JSON.stringify(heroSettings))
    if (files.profileImageFile) {
      formData.append('profile_image', files.profileImageFile)
    }

    const response = await api.put('/admin/hero-settings', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) return
        onUploadProgress(Math.round((event.loaded * 100) / event.total))
      },
    })

    return response.data
  },

  restoreHeroSettings: async (): Promise<HeroSettingsResponse> => {
    const response = await api.post('/admin/hero-settings/restore')
    return response.data
  },
}

// Recommendations API
export const recommendationsApi = {
  getRecommendations: async (params?: {
    skip?: number
    limit?: number
    recommendation_type?: string
  }): Promise<Recommendation[]> => {
    const response = await api.get('/recommendations', { params })
    return response.data
  },

  getAdminRecommendations: async (params?: {
    skip?: number
    limit?: number
    recommendation_type?: string
  }): Promise<Recommendation[]> => {
    const response = await api.get('/recommendations/admin', { params })
    return response.data
  },

  getAdminRecommendation: async (id: number | string): Promise<Recommendation> => {
    const response = await api.get(`/recommendations/admin/${id}`)
    return response.data
  },

  getRecommendation: async (id: number | string): Promise<Recommendation> => {
    const response = await api.get(`/recommendations/${id}`)
    return response.data
  },

  updateRecommendation: async (id: number, data: Partial<Recommendation>): Promise<Recommendation> => {
    const response = await api.put(`/recommendations/${id}`, data)
    return response.data
  },

  publishRecommendation: async (id: number): Promise<{ message: string }> => {
    const response = await api.put(`/recommendations/${id}/publish`)
    return response.data
  },

  unpublishRecommendation: async (id: number): Promise<{ message: string }> => {
    const response = await api.put(`/recommendations/${id}/unpublish`)
    return response.data
  },

  deleteRecommendation: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/recommendations/${id}`)
    return response.data
  },
}

export const blogsApi = {
  getPublishedBlogs: async (params?: {
    page?: number
    page_size?: number
    search?: string
    category?: string
  }): Promise<BlogListResponse> => {
    const response = await api.get('/blogs/published', { params })
    return response.data
  },

  getLatestBlogs: async (limit: number = 3): Promise<Blog[]> => {
    const response = await api.get('/blogs/published/latest', { params: { limit } })
    return response.data
  },

  getPublishedBlogBySlug: async (slug: string): Promise<Blog> => {
    const response = await api.get(`/blogs/published/${slug}`)
    return response.data
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/blogs/categories')
    return response.data
  },

  adminListBlogs: async (params?: {
    page?: number
    page_size?: number
    search?: string
    category?: string
    status?: 'draft' | 'published'
  }): Promise<BlogListResponse> => {
    const response = await api.get('/blogs/admin', { params })
    return response.data
  },

  adminGetBlog: async (blogId: number): Promise<Blog> => {
    const response = await api.get(`/blogs/admin/${blogId}`)
    return response.data
  },

  adminPreviewBlog: async (blogId: number): Promise<Blog> => {
    const response = await api.get(`/blogs/admin/${blogId}/preview`)
    return response.data
  },

  adminCreateBlog: async (data: {
    title: string
    slug?: string
    short_description?: string
    content: string
    category?: string
    tags?: string
    author?: string
    read_time?: string
    meta_title?: string
    meta_description?: string
    status: 'draft' | 'published'
    featured_image_file?: File | null
  }): Promise<Blog> => {
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('content', data.content)
    formData.append('status', data.status)
    if (data.slug) formData.append('slug', data.slug)
    if (data.short_description) formData.append('short_description', data.short_description)
    if (data.category) formData.append('category', data.category)
    if (data.tags) formData.append('tags', data.tags)
    if (data.author) formData.append('author', data.author)
    if (data.read_time) formData.append('read_time', data.read_time)
    if (data.meta_title) formData.append('meta_title', data.meta_title)
    if (data.meta_description) formData.append('meta_description', data.meta_description)
    if (data.featured_image_file) formData.append('featured_image_file', data.featured_image_file)

    const response = await api.post('/blogs/admin', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  adminUpdateBlog: async (
    blogId: number,
    data: {
      title: string
      slug?: string
      short_description?: string
      content: string
      category?: string
      tags?: string
      author?: string
      read_time?: string
      meta_title?: string
      meta_description?: string
      status: 'draft' | 'published'
      remove_featured_image?: boolean
      featured_image_file?: File | null
    }
  ): Promise<Blog> => {
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('content', data.content)
    formData.append('status', data.status)
    if (data.slug) formData.append('slug', data.slug)
    if (data.short_description) formData.append('short_description', data.short_description)
    if (data.category) formData.append('category', data.category)
    if (data.tags) formData.append('tags', data.tags)
    if (data.author) formData.append('author', data.author)
    if (data.read_time) formData.append('read_time', data.read_time)
    if (data.meta_title) formData.append('meta_title', data.meta_title)
    if (data.meta_description) formData.append('meta_description', data.meta_description)
    if (data.remove_featured_image) formData.append('remove_featured_image', 'true')
    if (data.featured_image_file) formData.append('featured_image_file', data.featured_image_file)

    const response = await api.put(`/blogs/admin/${blogId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  adminPublishBlog: async (blogId: number): Promise<Blog> => {
    const response = await api.put(`/blogs/admin/${blogId}/publish`)
    return response.data
  },

  adminUnpublishBlog: async (blogId: number): Promise<Blog> => {
    const response = await api.put(`/blogs/admin/${blogId}/unpublish`)
    return response.data
  },

  adminDeleteBlog: async (blogId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/blogs/admin/${blogId}`)
    return response.data
  },
}

// Subscriptions API
export const subscriptionsApi = {
  // Get all subscription plans
  getPlans: async (activeOnly: boolean = true): Promise<SubscriptionPlan[]> => {
    const response = await api.get(`/subscriptions/plans?active_only=${activeOnly}`)
    return response.data
  },

  // Get specific subscription plan
  getPlan: async (planId: number): Promise<SubscriptionPlan> => {
    const response = await api.get(`/subscriptions/plans/${planId}`)
    return response.data
  },

  // Get specific subscription plan by slug
  getPlanBySlug: async (planSlug: string): Promise<SubscriptionPlan> => {
    const response = await api.get(`/subscriptions/plans/by-slug/${planSlug}`)
    return response.data
  },

  // Get plans for comparison
  getPlansComparison: async (): Promise<{ plans: SubscriptionPlan[] }> => {
    const response = await api.get('/subscriptions/plans/compare/all')
    return response.data
  },

  // Get current user's subscription
  getCurrentSubscription: memoizePromise(async (): Promise<SubscriptionSummary> => {
    const response = await api.get('/subscriptions/my-subscription')
    return response.data
  }),

  // Lightweight plan-status check (never 404s) — used to lock/unlock the dashboard
  getSubscriptionStatus: memoizePromise(async (): Promise<{
    has_active_plan: boolean
    status: string
    plan_name?: string
    start_date?: string
    end_date?: string
    days_remaining?: number
    auto_renew?: boolean
  }> => {
    const response = await api.get('/subscriptions/my-subscription/status')
    return response.data
  }),

  // Get all of the current user's subscriptions (history), newest first
  getMySubscriptions: async (): Promise<Subscription[]> => {
    const response = await api.get('/subscriptions/my-subscriptions')
    return response.data
  },

  // Create new subscription
  createSubscription: async (subscriptionData: SubscriptionCreate): Promise<Subscription> => {
    subscriptionsApi.getCurrentSubscription.clear()
    subscriptionsApi.getSubscriptionStatus.clear()
    const response = await api.post('/subscriptions/subscribe', subscriptionData)
    return response.data
  },

  // Update current subscription
  updateSubscription: async (updateData: { status?: string; auto_renew?: boolean; end_date?: string }): Promise<Subscription> => {
    subscriptionsApi.getCurrentSubscription.clear()
    subscriptionsApi.getSubscriptionStatus.clear()
    const response = await api.put('/subscriptions/my-subscription', updateData)
    return response.data
  },

  // Cancel current subscription
  cancelSubscription: async (): Promise<{ message: string }> => {
    subscriptionsApi.getCurrentSubscription.clear()
    subscriptionsApi.getSubscriptionStatus.clear()
    const response = await api.post('/subscriptions/my-subscription/cancel')
    return response.data
  },

  // Public FAQs
  getFaqs: async (): Promise<{ id: number; question: string; answer: string }[]> => {
    const response = await api.get('/subscriptions/faqs')
    return response.data
  },
}

// Notifications API
export const notificationsApi = {
  getNotifications: async (params?: {
    skip?: number
    limit?: number
    unread_only?: boolean
  }): Promise<Notification[]> => {
    const response = await api.get('/notifications', { params })
    return response.data
  },

  markAsRead: async (id: number): Promise<void> => {
    await api.put(`/notifications/${id}/read`)
  },

  markAllAsRead: async (): Promise<void> => {
    await api.put('/notifications/mark-all-read')
  },

  deleteNotification: async (id: number): Promise<void> => {
    await api.delete(`/notifications/${id}`)
  },
}

// KYC API
export const kycApi = {
  getKYCStatus: memoizePromise(async (): Promise<KYCData | null> => {
    const response = await api.get('/kyc/status')
    return response.data
  }),

  fetchFromCams: async (dob?: string): Promise<{ kyc_found: boolean; dob_required?: boolean; kyc_data: any }> => {
    const url = dob ? `/kyc/fetch-from-cams?dob=${encodeURIComponent(dob)}` : '/kyc/fetch-from-cams'
    const response = await api.post(url)
    return response.data
  },

  confirmKYC: async (data: Partial<KYCData> & { date_of_birth?: unknown }): Promise<{ onboarding_step: string; kyc_status: string; kyc: KYCData }> => {
    // Discard any status fetched while confirmation is in flight as well as
    // any earlier status. The next dashboard/profile read must see the commit.
    kycApi.getKYCStatus.clear()
    try {
      const response = await api.post('/kyc/confirm', {
        ...data,
        date_of_birth: normalizeKYCDate(data.date_of_birth),
      })
      return response.data
    } finally {
      kycApi.getKYCStatus.clear()
    }
  },

  uploadDocument: async (file: File, documentType: string): Promise<string> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('document_type', documentType)
    const response = await api.post('/kyc/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.file_path
  },
}

// eSign API
export const esignApi = {
  initiate: async (): Promise<{ signing_url: string; document_id: string; esign_status: string }> => {
    const response = await api.post('/esign/initiate')
    return response.data
  },

  getStatus: memoizePromise(async (): Promise<{ esign_status: string; onboarding_step: string }> => {
    const response = await api.get('/esign/status')
    return response.data
  }),
}

// Payments API
export const paymentsApi = {
  createOrder: async (
    planId: number,
    amount: number,
    couponCode?: string,
  ): Promise<{
    order_id: string
    amount: number
    currency: string
    razorpay_key: string
    plan_name: string
    original_amount: number
    discount_amount: number
    coupon_code: string | null
  }> => {
    const response = await api.post('/payments/create-order', {
      plan_id: planId,
      amount,
      coupon_code: couponCode || null,
    })
    return response.data
  },

  verifyPayment: async (data: {
    razorpay_order_id: string
    razorpay_payment_id: string
    razorpay_signature: string
    plan_id: number
    amount: number
    coupon_code?: string
    discount_amount?: number
  }): Promise<{ success: boolean; message: string; subscription: any }> => {
    const response = await api.post('/payments/verify', data)
    return response.data
  },

  markFailed: async (razorpayOrderId: string, reason?: string): Promise<{ success: boolean }> => {
    const response = await api.post('/payments/mark-failed', {
      razorpay_order_id: razorpayOrderId,
      reason: reason || null,
    })
    return response.data
  },
}

// Coupons API
export const couponsApi = {
  validate: async (
    code: string,
    planId: number,
    amount: number,
  ): Promise<{
    valid: boolean
    code: string
    description: string | null
    discount_type: string
    discount_value: number
    discount_amount: number
    final_amount: number
  }> => {
    const response = await api.post('/coupons/validate', {
      code,
      plan_id: planId,
      amount,
    })
    return response.data
  },
}

export const contactApi = {
  submit: async (data: {
    name: string
    email: string
    phone: string
    subject: string
    message: string
  }): Promise<{ status: string }> => {
    const response = await api.post('/contact', data)
    return response.data
  },
}

export default api
